import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLocale } from "../context/LocaleContext";
import { shouldLocalizePath, translateUiText } from "../lib/uiTranslation";

const translatedText = new WeakMap();
const translatedAttributes = new WeakMap();
const LOCALIZED_ATTRIBUTES = ["aria-label", "placeholder", "title"];
const SKIP_SELECTOR = [
  "[translate='no']",
  ".notranslate",
  "[data-no-translate]",
  "[contenteditable='true']",
  "script",
  "style",
  "code",
  "pre",
].join(",");

const skipped = (node) => {
  const element = node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement;
  return Boolean(element?.closest(SKIP_SELECTOR));
};

const localizeTextNode = (node, locale) => {
  if (skipped(node)) return;
  const current = node.nodeValue || "";
  const previous = translatedText.get(node);
  if (locale !== "en") {
    if (previous && current === previous.translated) node.nodeValue = previous.original;
    translatedText.delete(node);
    return;
  }
  if (previous && current === previous.translated) return;
  const translated = translateUiText(current, locale);
  if (translated !== current) {
    translatedText.set(node, { original: current, translated });
    node.nodeValue = translated;
  }
};

const localizeElement = (element, locale) => {
  if (skipped(element)) return;
  const previous = translatedAttributes.get(element) || {};
  const next = { ...previous };
  for (const attribute of LOCALIZED_ATTRIBUTES) {
    if (!element.hasAttribute(attribute)) continue;
    const current = element.getAttribute(attribute) || "";
    const state = previous[attribute];
    if (locale !== "en") {
      if (state && current === state.translated) element.setAttribute(attribute, state.original);
      delete next[attribute];
      continue;
    }
    if (state && current === state.translated) continue;
    const translated = translateUiText(current, locale);
    if (translated !== current) {
      next[attribute] = { original: current, translated };
      element.setAttribute(attribute, translated);
    }
  }
  if (Object.keys(next).length) translatedAttributes.set(element, next);
  else translatedAttributes.delete(element);
};

const localizeTree = (root, locale) => {
  if (!root || skipped(root)) return;
  if (root.nodeType === Node.TEXT_NODE) {
    localizeTextNode(root, locale);
    return;
  }
  if (root.nodeType === Node.ELEMENT_NODE) localizeElement(root, locale);
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT,
  );
  let node = walker.nextNode();
  while (node) {
    if (node.nodeType === Node.TEXT_NODE) localizeTextNode(node, locale);
    else localizeElement(node, locale);
    node = walker.nextNode();
  }
};

export default function LocalizedDocument() {
  const { locale, ready } = useLocale();
  const { pathname } = useLocation();
  const enabled = ready && shouldLocalizePath(pathname);

  useEffect(() => {
    if (!ready) return undefined;
    const activeLocale = enabled ? locale : "id";
    localizeTree(document.body, activeLocale);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "characterData") localizeTextNode(mutation.target, activeLocale);
        if (mutation.type === "attributes") localizeElement(mutation.target, activeLocale);
        mutation.addedNodes.forEach((node) => localizeTree(node, activeLocale));
      }
    });
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: LOCALIZED_ATTRIBUTES,
    });

    const nativeConfirm = window.confirm;
    window.confirm = (message) => nativeConfirm(translateUiText(message, activeLocale));
    return () => {
      observer.disconnect();
      window.confirm = nativeConfirm;
    };
  }, [enabled, locale, pathname, ready]);

  return null;
}
